import i18n from "assests/locales/i18n";
import { checkIfHasServiceabilityError } from "lib/hooks/useCheckoutErrors";
import { CartDialogType } from "lib/types/cart";
import { useUserAddress } from "lib/hooks/useUserAddress";
import {
  ActionUrlsType,
  AddOnMetaType,
  AddOnType,
  BillingType,
  CheckoutSectionType,
  CheckoutErrorResponse,
  CheckoutExpiredType,
  CheckoutModalType,
  CheckoutValidationsType,
  CheckoutViewType,
  CombinedProductRemovalItemsType,
  CouponReplacementType,
  CouponType,
  DiscountProductSelectorModalType,
  DiscountedProductSelectorDataType,
  DiscountedProductSelectorPayloadType,
  ExitCouponNudgeRuntimeType,
  ExitSurveyOptionType,
  FloWalletRedemptionHandle,
  GiftCards,
  ItemLevelETDType,
  ItemType,
  LoyaltyRedemptionHandle,
  NoteAttributesType,
  ShippingHandleType,
  ShippingHandlesType,
} from "lib/types/checkout";
import { DiscountAchieverVariant } from "lib/types/discountAchiever";
import {
  getAppliedFloWalletData,
  getAppliedLoyaltyData,
  getBillingDetails,
  getCouponData,
  getDiscountedProductSelectorData,
  getDiscountedProductSelectorPayload,
  getGiftCardData,
  getItems,
  getRedirectUrl,
  showShippingChangeToast,
} from "lib/utils/checkout";
import { apiURI } from "lib/utils/constants";
import { getIsTwoStepCheckout } from "lib/utils/merchant";
import { errorToast, successToast } from "lib/utils/toasters";
import { createContext, useCallback, useContext, useMemo, useReducer, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { mutate } from "swr";
import { getGiftCardsUrl } from "lib/utils/giftCards";
import { useMerchantContext } from "./MerchantProvider";
import {
  InitialCheckoutStepType,
  PartnerOfferType,
  PartnerOffersDisplay,
} from "lib/types/merchantConfigTypes";
import { usePopupOrchestrator } from "./PopupOrchestratorProvider";
import PopupLoadPhaseGate from "./PopupLoadPhaseGate";

export interface AppliedCouponType {
  code: string;
  title: string; //Will be null for all cases except freebies
  autoApplied: boolean;
  isManualCode: boolean;
  isFreebie: boolean;
  discountValue: number;
  isRemovable: boolean;
  freebieItemCount: number;
  isPrepaid: boolean;
  prepaidValue: number;
  discountPercentage: number;
  isReward?: boolean;
  couponRewardConfig?: {
    walletType: "CRED" | "TICKERTAPE";
    amount: number;
    expiryConfig: any;
    couponId: string;
  };
  rewardData?: {
    header: string;
    description: string;
    longDescription: string[];
  };
  isProductSelector?: boolean;
  couponType?: CouponType;
}

const isCart = new URLSearchParams(window.location.search).get("page")?.toLowerCase() === "cart";

function shouldOpenFreeGiftsProductSelector(
  discounts: any[] | undefined,
  options?: { selectorQuantityUpdated?: boolean },
): boolean {
  if (!discounts?.length) {
    return false;
  }

  const hasPendingSelector = discounts.some((coupon: any) => {
    const showSelector =
      coupon?.show_selector === undefined || coupon?.show_selector === null || coupon?.show_selector === true;

    return (
      coupon?.coupon_metadata?.coupon_type === CouponType.BXGY &&
      coupon?.cart_alterations?.some(
        (alteration: any) =>
          alteration?.type === "SHOW_PRODUCT_SELECTOR" && alteration?.product_details?.length > 0,
      ) &&
      Boolean(coupon?.selected_items) &&
      coupon?.selected_items?.length === 0 &&
      showSelector
    );
  });

  return hasPendingSelector || Boolean(options?.selectorQuantityUpdated);
}

export interface AppliedGiftCardsType {
  cardId: string;
  cardValue: number;
  displayName: string;
}

const Context = createContext<CheckoutContext>({ state: {}, actions: {} } as CheckoutContext);

const defaultBillingDetails = {
  total_payable: 0,
  sub_total: 0,
  total: 0,
  tax: 0,
  rewards: 0,
  discount: 0,
  shipping: -1,
  cod: 0,
  cod_enabled: false,
  taxes_included: true,
  serviceable: true,
  prepaid_total: 0,
  prepaid_discount: 0,
  original_sub_total: 0,
  addon_total: 0,
  gift_card: 0,
};

export enum ActionType {
  SET_CHECKOUT = "SET_CHECKOUT",
  INITIALIZE = "INITIALIZE",
  RESET = "RESET",
  SET_LOADING = "SET_LOADING",
  CHECKOUT_UPDATE = "CHECKOUT_UPDATE",
  CHECKOUT_INIT = "CHECKOUT_INIT",
  CHECKOUT_RESPONSE = "CHECKOUT_RESPONSE",
  SET_BILLING = "SET_BILLING",
  SET_CHECKOUT_ID = "SET_CHECKOUT_ID",
  SET_ORDER_ID = "SET_ORDER_ID",
  SET_APPLIED_COUPONS = "SET_APPLIED_COUPONS",
  SET_APPLIED_GIFT_CARDS = "SET_APPLIED_GIFT_CARDS",
  SET_APPLIED_LOYALTY = "SET_APPLIED_LOYALTY",
  SET_APPLIED_WALLET_DATA = "SET_APPLIED_WALLET_DATA",
  SET_REDIRECT_URL = "SET_REDIRECT_URL",
  SET_ORIGIN_URL = "SET_ORIGIN_URL",
  SET_PG_ORDER_ID = "SET_PG_ORDER_ID",
  SET_BUY_NOW = "SET_BUY_NOW",
  SET_SHOPIFY_SESSION_ID = "SET_SHOPIFY_SESSION_ID",
  SET_CHECKOUT_TOKEN = "SET_CHECKOUT_TOKEN",
  SET_COUPONS = "SET_COUPONS",
  SET_BANK_OFFERS = "SET_BANK_OFFERS",
  SET_PARTNER_OFFERS = "SET_PARTNER_OFFERS",
  SET_IS_C2P = "SET_IS_C2P",
  SET_SHIPPING_HANDLES = "SET_SHIPPING_HANDLES",
  SET_CHECKOUT_VIEW = "SET_CHECKOUT_VIEW",
  SET_CHECKOUT_MODAL = "SET_CHECKOUT_MODAL",
  SET_IS_TWO_STEP_CHECKOUT = "SET_IS_TWO_STEP_CHECKOUT",
  SET_OOS_ITEMS = "SET_OOS_ITEMS",
  SET_CHECKOUT_ITEMS = "SET_CHECKOUT_ITEMS",
  SET_EXIT_SURVEY_OPTIONS = "SET_EXIT_SURVEY_OPTIONS",
  SET_CHECKOUT_UI_METADATA = "SET_CHECKOUT_UI_METADATA",
  SET_ACTION_URLS = "SET_ACTION_URLS",
  SET_DISCOUNTS_AND_REWARDS = "SET_DISCOUNTS_AND_REWARDS",
  SET_IS_BUY_NOW = "SET_IS_BUY_NOW",
  SET_CHECKOUT_EXPIRED = "SET_CHECKOUT_EXPIRED",
  SET_ACTIVE_COMPONENT = "SET_ACTIVE_COMPONENT",
  SET_CHECKOUT_VALIDATIONS = "SET_CHECKOUT_VALIDATIONS",
  SET_IS_ORDER_SUMMARY_OPEN = "SET_IS_ORDER_SUMMARY_OPEN",
  SET_ETD_TEXT = "SET_ETD_TEXT",
  SET_DISCOUNTED_PRODUCT_SELECTOR = "SET_DISCOUNTED_PRODUCT_SELECTOR",
  SET_DISCOUNT_PRODUCT_SELECTOR_PAYLOAD = "SET_DISCOUNT_PRODUCT_SELECTOR_PAYLOAD",
  SET_CART_DIALOG = "SET_CART_DIALOG",
  SET_DISCOUNT_PRODUCT_SELECTOR_MODAL = "SET_DISCOUNT_PRODUCT_SELECTOR_MODAL",
  SET_SHOW_ERROR_SCREEN = "SET_SHOW_ERROR_SCREEN",
  SET_HAS_COD_MODE = "SET_HAS_COD_MODE",
  SET_CHECKOUT_LOADING = "SET_CHECKOUT_LOADING",
  SET_INITIAL_CHECKOUT_STEP = "SET_INITIAL_CHECKOUT_STEP",
  SET_RAW_CHECKOUT_OBJECT = "SET_RAW_CHECKOUT_OBJECT",
  SET_FETCHED_CHECKOUT_ID = "SET_FETCHED_CHECKOUT_ID",
  SET_COMBINED_PRODUCT_REMOVAL_ITEMS = "SET_COMBINED_PRODUCT_REMOVAL_ITEMS",
  SET_COUPON_REPLACEMENT_CONFIG = "SET_COUPON_REPLACEMENT_CONFIG",
  SET_EXIT_COUPON_NUDGE_RUNTIME = "SET_EXIT_COUPON_NUDGE_RUNTIME",
  SET_ITEM_LEVEL_ETD = "SET_ITEM_LEVEL_ETD",
  SET_ITEM_VARIANTS = "SET_ITEM_VARIANTS",
  SET_PARTNER_OFFERS_DISPLAY = "SET_PARTNER_OFFERS_DISPLAY",
}

export type Action =
  | { type: ActionType.SET_CHECKOUT; payload: Partial<CheckoutReducerState> }
  | { type: ActionType.INITIALIZE; payload: CheckoutReducerState }
  | { type: ActionType.RESET; payload: CheckoutReducerState }
  | { type: ActionType.CHECKOUT_INIT; payload: any } // checkout response
  | { type: ActionType.CHECKOUT_RESPONSE; payload: any } // chekcout response
  | { type: ActionType.SET_LOADING; payload: boolean }
  | { type: ActionType.SET_IS_BUY_NOW; payload: boolean }
  | { type: ActionType.SET_CHECKOUT_ID; payload: string }
  | { type: ActionType.SET_CHECKOUT_TOKEN; payload: string }
  | { type: ActionType.SET_REDIRECT_URL; payload: string }
  | { type: ActionType.SET_ORIGIN_URL; payload: string }
  | { type: ActionType.SET_ORDER_ID; payload: string }
  | { type: ActionType.SET_SHOPIFY_SESSION_ID; payload: string }
  | { type: ActionType.SET_BILLING; payload: BillingType }
  | { type: ActionType.SET_DISCOUNTS_AND_REWARDS; payload: any } // checkout response
  | { type: ActionType.SET_COUPONS; payload: any }
  | { type: ActionType.SET_BANK_OFFERS; payload: any }
  | { type: ActionType.SET_PARTNER_OFFERS; payload: PartnerOfferType[] }
  | { type: ActionType.SET_PARTNER_OFFERS_DISPLAY; payload: PartnerOffersDisplay | null }
  | { type: ActionType.SET_CHECKOUT_VIEW; payload: CheckoutViewType }
  | { type: ActionType.SET_CHECKOUT_MODAL; payload: CheckoutModalType }
  | { type: ActionType.SET_OOS_ITEMS; payload: ItemType[] }
  | { type: ActionType.SET_CHECKOUT_ITEMS; payload: ItemType[] }
  | {
      type: ActionType.SET_SHIPPING_HANDLES;
      payload: ShippingHandlesType;
    }
  | { type: ActionType.SET_CHECKOUT_EXPIRED; payload: CheckoutExpiredType }
  | { type: ActionType.SET_ACTIVE_COMPONENT; payload: CheckoutSectionType }
  | { type: ActionType.SET_CHECKOUT_VALIDATIONS; payload: CheckoutValidationsType }
  | { type: ActionType.SET_IS_ORDER_SUMMARY_OPEN; payload: boolean }
  | { type: ActionType.SET_ETD_TEXT; payload: { addressId: string; text: string } }
  | { type: ActionType.SET_DISCOUNTED_PRODUCT_SELECTOR; payload: DiscountedProductSelectorDataType }
  | { type: ActionType.SET_DISCOUNT_PRODUCT_SELECTOR_PAYLOAD; payload: DiscountedProductSelectorPayloadType }
  | { type: ActionType.SET_DISCOUNT_PRODUCT_SELECTOR_MODAL; payload: DiscountProductSelectorModalType }
  | { type: ActionType.SET_SHOW_ERROR_SCREEN; payload: boolean }
  | { type: ActionType.SET_CART_DIALOG; payload: CartDialogType }
  | { type: ActionType.SET_HAS_COD_MODE; payload: boolean }
  | { type: ActionType.SET_CHECKOUT_LOADING; payload: boolean }
  | { type: ActionType.SET_INITIAL_CHECKOUT_STEP; payload: InitialCheckoutStepType }
  | { type: ActionType.SET_RAW_CHECKOUT_OBJECT; payload: any }
  | { type: ActionType.SET_FETCHED_CHECKOUT_ID; payload: string }
  | { type: ActionType.SET_COMBINED_PRODUCT_REMOVAL_ITEMS; payload: CombinedProductRemovalItemsType }
  | { type: ActionType.SET_COUPON_REPLACEMENT_CONFIG; payload: CouponReplacementType }
  | { type: ActionType.SET_EXIT_COUPON_NUDGE_RUNTIME; payload: ExitCouponNudgeRuntimeType | undefined }
  | { type: ActionType.SET_ITEM_LEVEL_ETD; payload: ItemLevelETDType }
  | { type: ActionType.SET_ITEM_VARIANTS; payload: Record<string, DiscountAchieverVariant[]> };

export interface CheckoutReducerState {
  isLoading: boolean;
  checkoutId: string;
  orderId: string;
  billing: BillingType;
  appliedCoupons: AppliedCouponType[];
  appliedGiftCards: AppliedGiftCardsType[];
  appliedLoyalty: LoyaltyRedemptionHandle;
  appliedWalletData: FloWalletRedemptionHandle;
  redirectUrl: string;
  originUrl: string;
  pgOrderId: string;
  isBuyNow: boolean;
  shopifySessionId: string;
  checkoutToken: string;
  coupons: any[];
  bankOffers: any[];
  partnerOffers: PartnerOfferType[];
  partnerOffersDisplay: PartnerOffersDisplay | null;
  isC2P: boolean;
  shippingHandles: ShippingHandlesType;
  checkoutView: CheckoutViewType;
  checkoutModal: CheckoutModalType;
  isTwoStepCheckout: boolean;
  oosItems: ItemType[];
  checkoutItems: ItemType[];
  exitSurveyOptions: ExitSurveyOptionType[];
  exitCouponNudgeRuntime?: ExitCouponNudgeRuntimeType;
  actionUrls?: ActionUrlsType;
  checkoutExpired?: CheckoutExpiredType;
  activeComponent: CheckoutSectionType;
  checkoutValidations: CheckoutValidationsType;
  segmentId: string;
  etdText: {
    [addressId: string]: string;
  };
  uid?: string;
  discountedProductSelectorData: DiscountedProductSelectorDataType;
  discountProductSelectorPayload: DiscountedProductSelectorPayloadType;
  discountProductSelectorModal: DiscountProductSelectorModalType;
  cartDialog: CartDialogType;
  showErrorScreen: boolean;
  appliedCartAddOns: AddOnType[];
  availableAddOns: Record<string, AddOnMetaType>;
  checkoutLoading: boolean;
  errorResponses: CheckoutErrorResponse[];
  rawCheckoutObject: any;
  /**
   * The checkout id the server last answered a checkout request for.
   *
   * Distinct from checkoutId, which is set from the login/token response and so
   * runs ahead of the server: logging in mints a checkout and names it here
   * before anything has been fetched for it. Callers that must not address a
   * checkout the server has not served should compare against this.
   */
  fetchedCheckoutId: string;
  /** Increments on every completed checkout request, so callers can tell a
   *  fresh fetch from one that merely happened to name the same id. */
  checkoutFetchSeq: number;
  noteAttributes: NoteAttributesType[];
  combinedProductRemovalItems: CombinedProductRemovalItemsType;
  couponReplacementConfig: CouponReplacementType;
  addresses: {
    shipping_address_id?: string;
    billing_address_id?: string;
  };
  merchantId: string;
  note: string;
  giftCards: GiftCards[];
  isFlashRedirect?: boolean; // Being done for flash redirection will be removed post channel specific config
  itemLevelETD: ItemLevelETDType;
  /** Sibling variants for every product in the cart, keyed by product_id */
  itemVariants: Record<string, DiscountAchieverVariant[]>;
}

export interface CheckoutState extends CheckoutReducerState {
  hasDefaultShippingHandleSelected: boolean;
}

export type SelectorRoutingIntent = "MAIN" | "AUTO";

export interface CheckoutContext {
  state: CheckoutState;
  actions: {
    checkoutInit: (response: any) => void;
    setShippingHandles: (shippingHanldes: ShippingHandlesType) => void;
    updateCheckoutBasedOnCheckoutResponse: (
      response: any,
      selectorIntent?: SelectorRoutingIntent,
      trustItemLevelAddOns?: boolean,
    ) => boolean;
    setCheckoutView: (view: CheckoutViewType, isFromPopState?: boolean) => void;
    setCheckoutModal: (modal: CheckoutModalType) => void;
    setOOSItems: (items: ItemType[]) => void;
    setCheckoutItems: (items: ItemType[]) => void;
    setCoupons: (coupons: any) => void;
    setBankOffers: (bankOffers: any) => void;
    setPartnerOffers: (offers: PartnerOfferType[]) => void;
    setPartnerOffersDisplay: (display: PartnerOffersDisplay | null) => void;
    setBilling: (billingDetails: BillingType) => void;
    updateCheckoutFromPayments: (response: any) => void;
    setCheckout: (data: Partial<CheckoutState>) => void;
    setCheckoutExpired: (expired: boolean, isInvalidUser: boolean) => void;
    setShowErrorScreen: (showErrorScreen: boolean) => void;
    setActiveComponent: (component: CheckoutSectionType) => void;
    setCheckoutValidations: (actions: CheckoutValidationsType) => void;
    setEtdText: (data: { addressId: string; text: string }) => void;
    setDiscountedProductSelectorData: (data: DiscountedProductSelectorDataType) => void;
    setDiscountProductSelectorPayload: (data: DiscountedProductSelectorPayloadType) => void;
    setDiscountProductSelectorModal: (
      modal: DiscountProductSelectorModalType,
      intent?: "MAIN" | "AUTO",
    ) => void;
    setCartDialog: (dialog: CartDialogType) => void;
    setCheckoutLoading: (loading: boolean) => void;
    setCheckoutId: (checkoutId: string) => void;
    setCombinedProductRemovalItems: (items: CombinedProductRemovalItemsType) => void;
    setCouponReplacementConfig: (config: CouponReplacementType) => void;
    setExitCouponNudgeRuntime: (runtime: ExitCouponNudgeRuntimeType | undefined) => void;
    setItemLevelETD: (items: ItemLevelETDType) => void;
    setItemVariants: (variants: Record<string, DiscountAchieverVariant[]>) => void;
    hasCelebrationPopupBeenShown: () => boolean;
    markCelebrationPopupShown: () => void;
  };
}

function reducer(state: CheckoutReducerState, action: Action): CheckoutReducerState {
  switch (action.type) {
    case ActionType.INITIALIZE: {
      return action.payload;
    }
    case ActionType.SET_LOADING: {
      return { ...state, isLoading: action.payload };
    }
    case ActionType.CHECKOUT_RESPONSE: {
      const {
        billingDetails,
        checkoutId,
        discounts,
        rewards,
        shippingHandles,
        checkoutToken,
        actionUrls,
        checkoutItems,
        segmentId,
        checkoutModal,
        discountProductSelectorModal,
        errorResponses,
        noteAttributes,
        note,
        giftCards,
        appliedCartAddOns,
        freshAddOns,
        trustItemLevelAddOns,
      } = action.payload;

      // See the comment at the CHECKOUT_RESPONSE dispatch site: this merge must read
      // `state` (always current at reduce time), not a closure snapshot, since two
      // dispatches from the same handler can land in the same batch before either
      // one has been reflected back into the caller's own state variable.
      const mergedAvailableAddOns = freshAddOns
        ? {
            ...(trustItemLevelAddOns
              ? Object.fromEntries(
                  Object.entries(freshAddOns).filter(
                    ([, addon]) => (addon as AddOnMetaType)?.addon_level === "ITEM",
                  ),
                )
              : Object.fromEntries(
                  Object.entries(state.availableAddOns ?? {}).filter(
                    ([, addon]) => (addon as AddOnMetaType)?.addon_level === "ITEM",
                  ),
                )),
            ...Object.fromEntries(
              Object.entries(freshAddOns).filter(
                ([, addon]) => (addon as AddOnMetaType)?.addon_level !== "ITEM",
              ),
            ),
          }
        : undefined;

      return {
        ...state,
        ...(checkoutId ? { checkoutId } : {}),
        ...(shippingHandles?.length ? { shippingHandles: shippingHandles } : {}),
        ...(checkoutToken ? { checkoutToken } : {}),
        ...(actionUrls ? { actionUrls } : {}),
        ...(checkoutModal ? { checkoutModal } : {}),
        ...(discountProductSelectorModal ? { discountProductSelectorModal } : {}),
        ...(errorResponses ? { errorResponses } : {}),
        ...(note ? { note } : { note: "" }),
        ...(giftCards ? { giftCards } : {}),
        ...(appliedCartAddOns ? { appliedCartAddOns } : {}),
        ...(mergedAvailableAddOns ? { availableAddOns: mergedAvailableAddOns } : {}),
        billing: billingDetails,
        appliedCoupons: getCouponData(discounts),
        discountedProductSelectorData: getDiscountedProductSelectorData(discounts),
        discountProductSelectorPayload: getDiscountedProductSelectorPayload(discounts),
        appliedGiftCards: getGiftCardData(rewards),
        appliedLoyalty: getAppliedLoyaltyData(rewards),
        appliedWalletData: getAppliedFloWalletData(rewards),
        checkoutItems: checkoutItems,
        ...(noteAttributes && { noteAttributes }),
        ...(segmentId && { segmentId }),
      };
    }
    case ActionType.SET_CHECKOUT: {
      const {
        checkoutId,
        orderId,
        billing,
        appliedCoupons,
        appliedGiftCards,
        appliedLoyalty,
        appliedWalletData,
        redirectUrl,
        originUrl,
        pgOrderId,
        isBuyNow,
        shopifySessionId,
        checkoutToken,
        coupons,
        bankOffers,
        partnerOffers,
        isC2P,
        shippingHandles,
        checkoutView,
        checkoutModal,
        isTwoStepCheckout,
        oosItems,
        checkoutItems,
        exitSurveyOptions,
        actionUrls,
        activeComponent,
        checkoutValidations,
        availableAddOns,
        appliedCartAddOns,
        noteAttributes,
        segmentId,
        giftCards,
      } = action.payload;

      return {
        ...state,
        ...(checkoutId ? { checkoutId } : {}),
        ...(orderId ? { orderId } : {}),
        ...(redirectUrl ? { redirectUrl } : {}),
        ...(originUrl ? { originUrl } : {}),
        ...(pgOrderId ? { pgOrderId } : {}),
        ...(typeof isBuyNow === "boolean" ? { isBuyNow } : {}),
        ...(shopifySessionId ? { shopifySessionId } : {}),
        ...(coupons ? { coupons } : {}),
        ...(bankOffers ? { bankOffers } : {}),
        ...(partnerOffers ? { partnerOffers } : {}),
        ...(typeof isC2P === "boolean" ? { isC2P } : {}),
        ...(checkoutView ? { checkoutView } : {}),
        ...(shippingHandles?.length ? { shippingHandles } : {}),
        ...(checkoutToken ? { checkoutToken } : {}),
        ...(billing ? { billing } : {}),
        ...(appliedCoupons ? { appliedCoupons } : {}),
        ...(appliedGiftCards ? { appliedGiftCards } : {}),
        ...(appliedLoyalty ? { appliedLoyalty } : {}),
        ...(appliedWalletData ? { appliedWalletData } : {}),
        ...(checkoutModal ? { checkoutModal } : {}),
        ...(typeof isTwoStepCheckout === "boolean" ? { isTwoStepCheckout } : {}),
        ...(oosItems ? { oosItems } : {}),
        ...(checkoutItems ? { checkoutItems } : {}),
        ...(exitSurveyOptions ? { exitSurveyOptions } : {}),
        ...(actionUrls ? { actionUrls } : {}),
        ...(activeComponent ? { activeComponent } : {}),
        ...(checkoutValidations ? { checkoutValidations } : {}),
        ...(availableAddOns ? { availableAddOns } : {}),
        ...(appliedCartAddOns ? { appliedCartAddOns } : {}),
        ...(noteAttributes ? { noteAttributes } : {}),
        ...(segmentId ? { segmentId } : {}),
        ...(giftCards ? { giftCards } : {}),
      };
    }
    case ActionType.CHECKOUT_INIT: {
      const response = action.payload;
      const checkout = response?.checkout;
      const token = response?.token;
      const merchantAttributes =
        response?.merchant_attributes?.account_attributes?.attributes ??
        response?.merchant_attributes?.attributes;

      const merchantId = response?.merchant_attributes?.uid ?? response?.merchant_attributes?.account_id;

      const isCheckoutC2P = Boolean(checkout?.metadata?.c2p?.enabled);
      const showShippingHandles = checkout?.metadata?.show_shipping_handle_selector ?? true;
      let checkoutInitOverrides = {};
      if (isCheckoutC2P && showShippingHandles) {
        checkoutInitOverrides = {
          checkoutView: "PAYMENTS",
        };
      }

      const exitSurveyOptions: ExitSurveyOptionType[] = checkout?.metadata?.survey_options?.map(
        (item: any) => {
          return {
            optionId: item?.key,
            optionLabel: item?.label,
          };
        },
      );

      const showProductSelector = shouldOpenFreeGiftsProductSelector(checkout?.discounts);

      let mainState = {
        ...state,
        actionUrls: checkout?.metadata?.action_urls,
        checkoutItems: getItems(checkout?.items),
        appliedCoupons: getCouponData(checkout?.discounts),
        discountedProductSelectorData: getDiscountedProductSelectorData(checkout?.discounts),
        discountProductSelectorModal: showProductSelector
          ? ("FREE_GIFTS" as DiscountProductSelectorModalType)
          : ("NONE" as DiscountProductSelectorModalType),
        discountProductSelectorPayload: getDiscountedProductSelectorPayload(checkout?.discounts),
        appliedGiftCards: getGiftCardData(checkout?.rewards),
        appliedLoyalty: getAppliedLoyaltyData(checkout?.rewards),
        appliedWalletData: getAppliedFloWalletData(checkout?.rewards),
        isC2P: isCheckoutC2P,
        shippingHandles: checkout?.metadata?.available_shipping_handles ?? [],
        exitSurveyOptions: exitSurveyOptions ?? state.exitSurveyOptions,
        uid: checkout?.uid,
        appliedCartAddOns: checkout?.cart_addons?.filter((item: AddOnType) => !item?.removed) ?? [],
        availableAddOns: checkout?.available_addons ?? {},
        errorResponses: checkout?.metadata?.error_responses ?? [],
        noteAttributes: checkout?.metadata?.note_attributes ?? [],
        addresses: {
          shipping_address_id: checkout?.addresses?.shipping_address_id,
          billing_address_id: checkout?.addresses?.billing_address_id,
        },
        merchantId: checkout?.merchant_id,
        giftCards: checkout?.gift_cards,
        note: checkout?.metadata?.note ?? "",
        isFlashRedirect: checkout?.metadata?.ui_config?.layout?.metadata?.is_flash_redirect ?? false,
      };

      if (token && merchantAttributes) {
        mainState = {
          ...mainState,
          redirectUrl: getRedirectUrl(
            checkout?.metadata?.redirect_url || checkout?.metadata?.back_url,
            merchantId,
          ),
          isBuyNow: Boolean(checkout?.metadata?.is_buy_now),
          originUrl: checkout?.metadata?.client_meta?.brand_url ?? merchantAttributes?.website_url,
          shopifySessionId: token?.sf_session_id,
          checkoutToken: token?.uid,
          ...checkoutInitOverrides,
        };
      }

      return mainState;
    }
    case ActionType.SET_DISCOUNTS_AND_REWARDS: {
      return {
        ...state,
        appliedCoupons: getCouponData(action.payload?.discounts),
        discountedProductSelectorData: getDiscountedProductSelectorData(action.payload?.discounts),
        discountProductSelectorPayload: getDiscountedProductSelectorPayload(action.payload?.discounts),
        appliedGiftCards: getGiftCardData(action.payload?.rewards),
        appliedLoyalty: getAppliedLoyaltyData(action.payload?.rewards),
        appliedWalletData: getAppliedFloWalletData(action.payload?.rewards),
      };
    }
    case ActionType.SET_BILLING: {
      return {
        ...state,
        billing: action.payload ?? defaultBillingDetails,
      };
    }
    case ActionType.SET_REDIRECT_URL: {
      return {
        ...state,
        redirectUrl: action.payload,
      };
    }
    case ActionType.SET_ORIGIN_URL: {
      return {
        ...state,
        originUrl: action.payload,
      };
    }
    case ActionType.SET_COUPONS: {
      return {
        ...state,
        coupons: action.payload,
      };
    }
    case ActionType.SET_BANK_OFFERS: {
      return {
        ...state,
        bankOffers: action.payload,
      };
    }
    case ActionType.SET_PARTNER_OFFERS: {
      return {
        ...state,
        partnerOffers: action.payload,
      };
    }
    case ActionType.SET_PARTNER_OFFERS_DISPLAY: {
      return {
        ...state,
        partnerOffersDisplay: action.payload,
      };
    }
    case ActionType.SET_SHIPPING_HANDLES: {
      return {
        ...state,
        shippingHandles: action.payload,
      };
    }
    case ActionType.SET_CHECKOUT_TOKEN: {
      return {
        ...state,
        checkoutToken: action.payload,
      };
    }
    case ActionType.SET_CHECKOUT_ID: {
      return { ...state, checkoutId: action.payload };
    }
    case ActionType.SET_CHECKOUT_VIEW: {
      return { ...state, checkoutView: action.payload };
    }
    case ActionType.SET_CHECKOUT_MODAL: {
      return { ...state, checkoutModal: action.payload };
    }
    case ActionType.SET_OOS_ITEMS: {
      return { ...state, oosItems: action.payload };
    }
    case ActionType.SET_CHECKOUT_ITEMS: {
      return { ...state, checkoutItems: action.payload };
    }
    case ActionType.SET_CHECKOUT_EXPIRED: {
      return { ...state, checkoutExpired: action.payload };
    }
    case ActionType.SET_SHOW_ERROR_SCREEN: {
      return { ...state, showErrorScreen: action.payload };
    }
    case ActionType.SET_ACTIVE_COMPONENT: {
      return { ...state, activeComponent: action.payload };
    }
    case ActionType.SET_CHECKOUT_VALIDATIONS: {
      return { ...state, checkoutValidations: action.payload };
    }
    case ActionType.SET_ETD_TEXT: {
      return {
        ...state,
        etdText: {
          ...state.etdText,
          [action.payload.addressId]: action.payload.text,
        },
      };
    }
    case ActionType.SET_DISCOUNTED_PRODUCT_SELECTOR: {
      return { ...state, discountedProductSelectorData: action.payload };
    }
    case ActionType.SET_DISCOUNT_PRODUCT_SELECTOR_PAYLOAD: {
      return { ...state, discountProductSelectorPayload: action.payload };
    }
    case ActionType.SET_CART_DIALOG: {
      return { ...state, cartDialog: action.payload };
    }
    case ActionType.SET_DISCOUNT_PRODUCT_SELECTOR_MODAL: {
      return { ...state, discountProductSelectorModal: action.payload };
    }
    case ActionType.SET_CHECKOUT_LOADING: {
      return { ...state, checkoutLoading: action.payload };
    }
    case ActionType.SET_RAW_CHECKOUT_OBJECT: {
      return {
        ...state,
        rawCheckoutObject: action.payload,
      };
    }
    case ActionType.SET_FETCHED_CHECKOUT_ID: {
      return {
        ...state,
        fetchedCheckoutId: action.payload,
        checkoutFetchSeq: state.checkoutFetchSeq + 1,
      };
    }
    case ActionType.SET_COMBINED_PRODUCT_REMOVAL_ITEMS: {
      return { ...state, combinedProductRemovalItems: action.payload };
    }
    case ActionType.SET_COUPON_REPLACEMENT_CONFIG: {
      return { ...state, couponReplacementConfig: action.payload };
    }
    case ActionType.SET_EXIT_COUPON_NUDGE_RUNTIME: {
      return { ...state, exitCouponNudgeRuntime: action.payload };
    }
    case ActionType.SET_ITEM_LEVEL_ETD: {
      return { ...state, itemLevelETD: action.payload };
    }
    case ActionType.SET_ITEM_VARIANTS: {
      return { ...state, itemVariants: { ...state.itemVariants, ...action.payload } };
    }
  }
  return state;
}

const checkoutInitialState: CheckoutReducerState = {
  showErrorScreen: false,
  isLoading: true,
  checkoutId: "",
  orderId: "",
  billing: defaultBillingDetails,
  appliedCoupons: [],
  appliedGiftCards: [],
  appliedLoyalty: {
    id: "",
    title: "",
    desc: "",
    totalPoints: 0,
    totalPrice: 0,
    provider: "NONE",
  },
  appliedWalletData: {
    earnMultiplier: 1,
    totalReducedPoints: 0,
    totalReductionAmount: 0,
  },
  redirectUrl: "",
  originUrl: "",
  pgOrderId: "",
  isBuyNow: false,
  shopifySessionId: "",
  checkoutToken: "",
  coupons: [],
  bankOffers: [],
  partnerOffers: [],
  partnerOffersDisplay: null,
  isC2P: false,
  shippingHandles: [],
  checkoutView: "NA" as CheckoutViewType,
  checkoutModal: "NONE" as CheckoutModalType,
  isTwoStepCheckout: false,
  oosItems: [],
  checkoutItems: [],
  exitSurveyOptions: [],
  activeComponent: "NONE",
  checkoutValidations: {
    address: {
      isValid: true,
    },
    shipping: {
      isValid: true,
    },
    cartLoginPopup: {
      isValid: true,
    },
  },
  etdText: {},
  uid: "",
  discountedProductSelectorData: {
    items: [],
    totalItems: 0,
    totalSelectedItems: 0,
  },
  discountProductSelectorPayload: {
    selection: [],
  },
  discountProductSelectorModal: "NONE",
  cartDialog: null,
  segmentId: "",
  appliedCartAddOns: [],
  availableAddOns: {},
  errorResponses: [],
  checkoutLoading: true,
  rawCheckoutObject: {},
  fetchedCheckoutId: "",
  checkoutFetchSeq: 0,
  noteAttributes: [],
  combinedProductRemovalItems: {
    selectedItem: {
      id: "",
      variantId: "",
    },
    removableVariantIds: [],
  },
  couponReplacementConfig: {
    showPopup: false,
    appliedDiscountCode: "",
    invalidDiscountCodes: [],
    invalidityReason: "NONE",
    appliedCouponTitle: "",
  },
  addresses: {},
  merchantId: "",
  note: "",
  giftCards: [],
  itemLevelETD: [],
  itemVariants: {},
};

export const CheckoutProvider: React.FC<React.PropsWithChildren<{ initialState?: CheckoutReducerState }>> = ({
  initialState = checkoutInitialState,
  children,
}) => {
  const {
    state: { checkoutMetadata, addressConfig },
  } = useMerchantContext();
  const { initialCheckoutStep } = checkoutMetadata ?? {};

  const [reducerState, dispatch] = useReducer(reducer, initialState);
  const [searchParams] = useSearchParams();
  const { openPopup, enqueueAuto, closePopup, activePopup } = usePopupOrchestrator();
  const hasEnqueuedInitialSelectorRef = useRef(false);
  const hasCelebrationPopupShownRef = useRef(false);

  const hasCelebrationPopupBeenShown = useCallback(() => hasCelebrationPopupShownRef.current, []);

  const markCelebrationPopupShown = useCallback(() => {
    hasCelebrationPopupShownRef.current = true;
  }, []);

  const getSelectorPosition = useCallback(
    () => (searchParams.get("page")?.toLowerCase() === "cart" ? "cart" : "checkout"),
    [searchParams],
  );

  // Checkout load is the only AUTO enqueue path; all other opens use MAIN.
  const routeDiscountProductSelector = useCallback(
    (mode: Exclude<DiscountProductSelectorModalType, "NONE">, intent: "MAIN" | "AUTO") => {
      const payload = { mode, position: getSelectorPosition() } as const;

      if (intent === "AUTO") {
        if (hasEnqueuedInitialSelectorRef.current) {
          return;
        }
        hasEnqueuedInitialSelectorRef.current = true;
        enqueueAuto("DISCOUNTED_PRODUCT_SELECTOR", payload);
        return;
      }

      openPopup("DISCOUNTED_PRODUCT_SELECTOR", payload);
    },
    [openPopup, enqueueAuto, getSelectorPosition],
  );

  const closeDiscountProductSelector = useCallback(() => {
    if (activePopup?.key === "DISCOUNTED_PRODUCT_SELECTOR") {
      closePopup();
    }
  }, [activePopup, closePopup]);

  const setDiscountProductSelectorModal = useCallback(
    (modal: DiscountProductSelectorModalType, intent: "MAIN" | "AUTO" = "MAIN") => {
      dispatch({
        type: ActionType.SET_DISCOUNT_PRODUCT_SELECTOR_MODAL,
        payload: modal,
      });

      if (modal === "NONE") {
        closeDiscountProductSelector();
        return;
      }

      routeDiscountProductSelector(modal, intent);
    },
    [closeDiscountProductSelector, routeDiscountProductSelector],
  );

  const updateCheckoutBasedOnCheckoutResponse = useCallback(
    (
      response: any,
      selectorIntent: SelectorRoutingIntent = "MAIN",
      trustItemLevelAddOns: boolean = false,
    ): boolean => {
      if (!response) return false;
      const page = searchParams.get("page")?.toLowerCase() ?? "";
      let payload: any = {};
      if (response.pricing) {
        const billingDetails = getBillingDetails(response.pricing) ?? defaultBillingDetails;
        payload = { ...payload, billingDetails };
        if (
          page !== "cart" &&
          showShippingChangeToast(reducerState.billing.shipping, billingDetails.shipping) &&
          !checkIfHasServiceabilityError(response?.metadata?.error_responses as CheckoutErrorResponse[])
        ) {
          successToast(i18n.t("shipping_fee_updated"));
        }
      }
      if (response.checkout_token_id) {
        payload = { ...payload, tokenId: response.checkout_token_id };
      }
      if (response.uid) {
        payload = { ...payload, checkoutId: response.uid };
      }
      if (response.rewards) {
        payload = {
          ...payload,
          rewards: response.rewards,
        };
      }
      if (Boolean(response?.items)) {
        payload = {
          ...payload,
          checkoutItems: getItems(response?.items),
        };
      }
      if (Boolean(response?.metadata?.action_urls)) {
        payload = { ...payload, actionUrls: response.metadata.action_urls };
      }

      const currentProductSelectorQuantity = reducerState?.discountedProductSelectorData?.totalItems;
      let updatedProductSelectorQuantity = 0;
      response?.discounts?.forEach((coupon: any) => {
        if (coupon?.coupon_metadata?.coupon_type === CouponType.BXGY) {
          coupon?.cart_alterations?.some((alteration: any) => {
            if (alteration?.type === "SHOW_PRODUCT_SELECTOR" && alteration?.product_details?.length > 0) {
              updatedProductSelectorQuantity += alteration?.quantity;
            }
          });
        }
      });

      const selectorQuantityUpdated =
        updatedProductSelectorQuantity > currentProductSelectorQuantity &&
        Boolean(currentProductSelectorQuantity);

      const showProductSelector = shouldOpenFreeGiftsProductSelector(response?.discounts, {
        selectorQuantityUpdated,
      });
      payload = {
        ...payload,
        discountProductSelectorModal: showProductSelector ? "FREE_GIFTS" : "NONE",
      };

      payload = {
        ...payload,
        discounts: response.discounts,
        giftCards: response?.gift_cards ?? [],
        shippingHandles: response.metadata.available_shipping_handles,
        ...(response?.segment_id && { segmentId: response?.segment_id }),
      };
      if (response?.cart_addons && response?.available_addons) {
        // Item-level add-on eligibility (addon_reference_id) is keyed by cart item
        // position, so it's only safe to trust a response's item-level entries when
        // that response itself reflects the current item arrangement (ie. it came
        // back from an /items mutation). Other mutations (coupons, gift cards, etc.)
        // can return available_addons without recomputing item-level eligibility, so
        // for those we keep whatever item-level entries were already in state.
        // The merge itself happens in the reducer (via freshAddOns/trustItemLevelAddOns
        // below) rather than here, because `reducerState` is a snapshot from this
        // render - a caller that fires multiple dispatches back to back in the same
        // tick (eg. an add-on mutation immediately followed by handleMiscUpdates) would
        // otherwise merge against a stale pre-dispatch value and clobber whatever the
        // earlier dispatch just set.
        payload = {
          ...payload,
          appliedCartAddOns: response?.cart_addons?.filter((item: AddOnType) => !item?.removed) ?? [],
          freshAddOns: response?.available_addons ?? {},
          trustItemLevelAddOns,
        };
      }
      if (response?.metadata?.error_responses) {
        payload = {
          ...payload,
          errorResponses: response?.metadata?.error_responses,
        };
      }

      if (response?.metadata?.error_responses) {
        payload = {
          ...payload,
          errorResponses: response?.metadata?.error_responses,
        };
      }
      if (response?.metadata?.note_attributes) {
        payload = {
          ...payload,
          noteAttributes: response?.metadata?.note_attributes ?? [],
        };
      }
      if (response?.metadata?.note) {
        payload = {
          ...payload,
          note: response?.metadata?.note ?? "",
        };
      }
      dispatch({
        type: ActionType.CHECKOUT_RESPONSE,
        payload,
      });
      dispatch({
        type: ActionType.SET_RAW_CHECKOUT_OBJECT,
        payload: response,
      });
      if (response?.uid) {
        dispatch({
          type: ActionType.SET_FETCHED_CHECKOUT_ID,
          payload: response.uid,
        });
      }

      if (showProductSelector) {
        if (selectorIntent === "MAIN") {
          routeDiscountProductSelector("FREE_GIFTS", "MAIN");
        }
      } else {
        closeDiscountProductSelector();
      }

      return showProductSelector;
    },
    [reducerState, routeDiscountProductSelector, closeDiscountProductSelector],
  );

  const updateCheckoutFromPayments = useCallback(
    (response: any) => {
      updateCheckoutBasedOnCheckoutResponse(response);
      let payload = {};
      if (Boolean(response?.items)) {
        payload = {
          ...payload,
          checkoutItems: getItems(response?.items),
        };
      }
      const isAddressServiceable = response?.pricing?.serviceable ?? false;
      if (!Boolean(isAddressServiceable) && Boolean(reducerState.checkoutView === "PAYMENTS")) {
        payload = {
          ...payload,
          ...(initialCheckoutStep === "PAYMENTS"
            ? { activeComponent: "ADDRESS_SECTION" }
            : { checkoutView: "ADDRESS_LIST" }),
          checkoutToken: response?.checkout_token_id ?? "",
        };
        errorToast(i18n.t("serviceability_error"), 5000);
      }

      const shippingHandles = response?.metadata?.available_shipping_handles;
      const showShippingHandles = response?.metadata?.show_shipping_handle_selector ?? false;
      payload = {
        ...payload,
        shippingHandles,
      };
      if (Boolean(showShippingHandles) && reducerState.checkoutView === "PAYMENTS") {
        payload = {
          ...payload,
          shippingHandles: reducerState.shippingHandles,
          ...(initialCheckoutStep === "PAYMENTS" ? { activeComponent: "SHIPPING_SECTION" } : {}),
        };
        if (initialCheckoutStep !== "PAYMENTS") {
          openPopup("SHIPPING_HANDLES", {});
        }
      } else {
        mutate(`/checkout/v2/checkout/${reducerState.checkoutId}/payments`);
        mutate(`/checkout/${reducerState.checkoutId}/rewards`);
        mutate(getGiftCardsUrl(reducerState.checkoutId));
        mutate(`UPI_INTENT`);
      }
      dispatch({
        type: ActionType.SET_CHECKOUT,
        payload,
      });
    },
    [reducerState, updateCheckoutBasedOnCheckoutResponse, initialCheckoutStep, openPopup],
  );

  const checkoutInit = useCallback(
    (tokenResponse: any) => {
      hasCelebrationPopupShownRef.current = false;
      dispatch({
        type: ActionType.CHECKOUT_INIT,
        payload: tokenResponse,
      });
      dispatch({
        type: ActionType.SET_RAW_CHECKOUT_OBJECT,
        payload: tokenResponse?.checkout,
      });

      if (shouldOpenFreeGiftsProductSelector(tokenResponse?.checkout?.discounts)) {
        routeDiscountProductSelector("FREE_GIFTS", "AUTO");
      } else {
        closeDiscountProductSelector();
      }

      const isC2P = Boolean(tokenResponse?.checkout?.metadata?.c2p?.enabled);
      const showShippingHandles = tokenResponse?.checkout?.metadata?.show_shipping_handle_selector ?? true;
      if (isC2P && showShippingHandles) {
        enqueueAuto("SHIPPING_HANDLES", {});
      }
    },
    [routeDiscountProductSelector, closeDiscountProductSelector, enqueueAuto],
  );

  const setShippingHandles = useCallback((shippingHanldes: ShippingHandlesType) => {
    dispatch({
      type: ActionType.SET_SHIPPING_HANDLES,
      payload: shippingHanldes,
    });
  }, []);

  const setCheckoutView = (view: CheckoutViewType) => {
    dispatch({
      type: ActionType.SET_CHECKOUT_VIEW,
      payload: view,
    });
  };

  const setCheckoutModal = (modal: CheckoutModalType) => {
    dispatch({
      type: ActionType.SET_CHECKOUT_MODAL,
      payload: modal,
    });
  };

  const setOOSItems = useCallback((items: ItemType[]) => {
    dispatch({
      type: ActionType.SET_OOS_ITEMS,
      payload: items,
    });
  }, []);

  const setCheckoutItems = useCallback((items: ItemType[]) => {
    dispatch({
      type: ActionType.SET_CHECKOUT_ITEMS,
      payload: items,
    });
  }, []);

  const setCheckoutId = useCallback((checkoutId: string) => {
    dispatch({
      type: ActionType.SET_CHECKOUT_ID,
      payload: checkoutId,
    });
  }, []);

  const setCoupons = useCallback((items: ItemType[]) => {
    dispatch({
      type: ActionType.SET_COUPONS,
      payload: items,
    });
  }, []);

  const setBankOffers = useCallback((offers: any) => {
    dispatch({
      type: ActionType.SET_BANK_OFFERS,
      payload: offers,
    });
  }, []);

  const setPartnerOffers = useCallback((offers: PartnerOfferType[]) => {
    dispatch({
      type: ActionType.SET_PARTNER_OFFERS,
      payload: offers,
    });
  }, []);

  const setPartnerOffersDisplay = useCallback((display: PartnerOffersDisplay | null) => {
    dispatch({
      type: ActionType.SET_PARTNER_OFFERS_DISPLAY,
      payload: display,
    });
  }, []);

  const setBilling = useCallback((billingDetails: BillingType) => {
    dispatch({
      type: ActionType.SET_BILLING,
      payload: billingDetails,
    });
  }, []);

  const setCheckoutExpired = useCallback((expired: boolean, isInvalidUser: boolean) => {
    dispatch({
      type: ActionType.SET_CHECKOUT_EXPIRED,
      payload: { expired, isInvalidUser },
    });
  }, []);

  const setShowErrorScreen = useCallback((showErrorScreen: boolean) => {
    dispatch({
      type: ActionType.SET_SHOW_ERROR_SCREEN,
      payload: showErrorScreen,
    });
  }, []);

  const setCheckout = useCallback((data: Partial<CheckoutState>) => {
    dispatch({
      type: ActionType.SET_CHECKOUT,
      payload: data,
    });
  }, []);

  const setEtdText = useCallback((data: { addressId: string; text: string }) => {
    dispatch({
      type: ActionType.SET_ETD_TEXT,
      payload: data,
    });
  }, []);

  const setActiveComponent = useCallback((data: CheckoutSectionType) => {
    dispatch({
      type: ActionType.SET_ACTIVE_COMPONENT,
      payload: data,
    });
  }, []);

  const setCheckoutValidations = useCallback((data: CheckoutValidationsType) => {
    dispatch({
      type: ActionType.SET_CHECKOUT_VALIDATIONS,
      payload: data,
    });
  }, []);

  const setDiscountedProductSelectorData = useCallback((data: DiscountedProductSelectorDataType) => {
    dispatch({
      type: ActionType.SET_DISCOUNTED_PRODUCT_SELECTOR,
      payload: data,
    });
  }, []);

  const setDiscountProductSelectorPayload = useCallback((data: DiscountedProductSelectorPayloadType) => {
    dispatch({
      type: ActionType.SET_DISCOUNT_PRODUCT_SELECTOR_PAYLOAD,
      payload: data,
    });
  }, []);

  const setCartDialog = useCallback((dialog: CartDialogType) => {
    dispatch({
      type: ActionType.SET_CART_DIALOG,
      payload: dialog,
    });
  }, []);

  const setCheckoutLoading = useCallback((loading: boolean) => {
    dispatch({
      type: ActionType.SET_CHECKOUT_LOADING,
      payload: loading,
    });
  }, []);

  const hasDefaultShippingHandleSelected = useMemo(() => {
    if (!Boolean(reducerState.shippingHandles) || !Boolean(reducerState.shippingHandles.length)) return true;
    const hasDefault = reducerState.shippingHandles.some(
      (handle: ShippingHandleType) => handle.selected_handle,
    );
    return hasDefault;
  }, [reducerState]);

  const state = useMemo(
    () => ({
      ...reducerState,
      hasDefaultShippingHandleSelected,
    }),
    [reducerState, hasDefaultShippingHandleSelected, isCart],
  );

  const setCombinedProductRemovalItems = useCallback((items: CombinedProductRemovalItemsType) => {
    dispatch({
      type: ActionType.SET_COMBINED_PRODUCT_REMOVAL_ITEMS,
      payload: items,
    });
  }, []);

  const setCouponReplacementConfig = useCallback((config: CouponReplacementType) => {
    dispatch({
      type: ActionType.SET_COUPON_REPLACEMENT_CONFIG,
      payload: config,
    });
  }, []);

  const setExitCouponNudgeRuntime = useCallback((runtime: ExitCouponNudgeRuntimeType | undefined) => {
    dispatch({
      type: ActionType.SET_EXIT_COUPON_NUDGE_RUNTIME,
      payload: runtime,
    });
  }, []);

  const setItemLevelETD = useCallback((items: ItemLevelETDType) => {
    dispatch({
      type: ActionType.SET_ITEM_LEVEL_ETD,
      payload: items,
    });
  }, []);

  const setItemVariants = useCallback((variants: Record<string, DiscountAchieverVariant[]>) => {
    dispatch({
      type: ActionType.SET_ITEM_VARIANTS,
      payload: variants,
    });
  }, []);

  useUserAddress({
    checkoutId: reducerState.checkoutId,
    merchantId: reducerState.merchantId,
    checkoutAddresses: reducerState.addresses,
    isCheckoutLoading: reducerState.checkoutLoading,
    addressConfig,
  });

  const actions = useMemo(
    () => ({
      updateCheckoutBasedOnCheckoutResponse,
      updateCheckoutFromPayments,
      setShippingHandles,
      checkoutInit,
      setCheckoutView,
      setCheckoutModal,
      setOOSItems,
      setCheckoutItems,
      setCoupons,
      setBankOffers,
      setPartnerOffers,
      setPartnerOffersDisplay,
      setBilling,
      setCheckout,
      setCheckoutExpired,
      setShowErrorScreen,
      setActiveComponent,
      setCheckoutValidations,
      setEtdText,
      setDiscountedProductSelectorData,
      setDiscountProductSelectorPayload,
      setCartDialog,
      setDiscountProductSelectorModal,
      setCheckoutLoading,
      setCheckoutId,
      setCombinedProductRemovalItems,
      setCouponReplacementConfig,
      setExitCouponNudgeRuntime,
      setItemLevelETD,
      setItemVariants,
      hasCelebrationPopupBeenShown,
      markCelebrationPopupShown,
    }),
    [
      updateCheckoutBasedOnCheckoutResponse,
      updateCheckoutFromPayments,
      setShippingHandles,
      checkoutInit,
      setCheckoutView,
      setCheckoutModal,
      setOOSItems,
      setCheckoutItems,
      setCoupons,
      setBankOffers,
      setPartnerOffers,
      setPartnerOffersDisplay,
      setBilling,
      setCheckout,
      setCheckoutExpired,
      setShowErrorScreen,
      setActiveComponent,
      setCheckoutValidations,
      setEtdText,
      setDiscountedProductSelectorData,
      setDiscountProductSelectorPayload,
      setCartDialog,
      setDiscountProductSelectorModal,
      setCheckoutLoading,
      setCheckoutId,
      setCombinedProductRemovalItems,
      setCouponReplacementConfig,
      setExitCouponNudgeRuntime,
      setItemLevelETD,
      setItemVariants,
      hasCelebrationPopupBeenShown,
      markCelebrationPopupShown,
    ],
  );

  return (
    <Context.Provider
      value={{
        state,
        actions,
      }}>
      <PopupLoadPhaseGate />
      {children}
    </Context.Provider>
  );
};

export function useCheckoutContext() {
  if (!Boolean(Context)) throw new Error("useCheckoutContext must be used within a CheckoutProvider");
  const checkoutContext = useContext(Context);
  return checkoutContext as CheckoutContext;
}
